import { getHttpServiceMeta, type HttpParamSource, type HttpVerb } from '@lingvo-news/contracts';
import { getFromApi, postToApi } from '../requests/request-helpers';

export interface SendRequest {
	(request: { readonly method: HttpVerb; readonly url: string; readonly body?: unknown }): Promise<unknown>;
}

export interface CreateClientOptions {
	readonly baseUrl?: string;
	readonly sendRequest?: SendRequest;
}

function normalizeJoin(baseUrl: string, basePath: string, path: string): string {
	const a = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
	const b1 = basePath.startsWith('/') ? basePath : `/${basePath}`;
	const b2 = path.startsWith('/') ? path : `/${path}`;
	return `${a}${b1}${b2}`;
}

function getParamPlan(
	paramByIndex: Readonly<Record<number, HttpParamSource>> | undefined,
	argsLength: number
): ReadonlyArray<{ readonly index: number; readonly source: HttpParamSource }> {
	const maxIndex = paramByIndex ? Math.max(-1, ...Object.keys(paramByIndex).map((k) => Number(k))) : -1;
	const count = Math.max(argsLength, maxIndex + 1, 0);
	const plan: Array<{ index: number; source: HttpParamSource }> = [];
	for (let i = 0; i < count; i += 1) {
		const source: HttpParamSource = paramByIndex?.[i] ?? { kind: 'arg' };
		plan.push({ index: i, source });
	}
	return plan;
}

function defaultSendRequest(req: { readonly method: HttpVerb; readonly url: string; readonly body?: unknown }): Promise<unknown> {
	if (req.method === 'GET') return getFromApi(req.url);
	if (req.method === 'POST') return postToApi(req.url, req.body ?? null);
	// Exhaustive
	return Promise.reject(new Error(`Unsupported method ${req.method}`));
}

export function createClient<TService>(
	contract: Function,
	options: CreateClientOptions = {}
): TService {
	const meta = getHttpServiceMeta(contract);
	const baseUrl = options.baseUrl ?? '';
	const sendRequest = options.sendRequest ?? defaultSendRequest;

	const proxy = new Proxy(
		{},
		{
			get(_target, prop) {
				// Prevent await/proxy Promise confusion
				if (prop === 'then') return undefined;
				if (typeof prop !== 'string') return undefined;

				const methodMeta = meta.methods[prop];
				if (!methodMeta) return undefined;

				return async (...args: unknown[]) => {
					const basePath = meta.basePath ?? '';
					const urlBase = normalizeJoin(baseUrl, basePath, methodMeta.path ?? `/${prop}`);

					const plan = getParamPlan(meta.params[prop], args.length);

					if (methodMeta.verb === 'GET') {
						const params = new URLSearchParams();
						for (const { index, source } of plan) {
							if (source.kind === 'userId' || source.kind === 'userIdOptional') continue;
							const value = args[index];

							const name = source.kind === 'arg' ? (source.name ?? `arg${index}`) : `arg${index}`;
							const json = JSON.stringify(value);
							if (json === undefined) continue;
							params.set(name, json);
						}
						const url = params.size > 0 ? `${urlBase}?${params.toString()}` : urlBase;
						return await sendRequest({ method: 'GET', url });
					}

					// POST
					const bodyArg = plan.find((p) => p.source.kind === 'body');
					if (bodyArg) {
						// When a method uses @httpBodyArg(), the request body is sent as-is.
						// Mixing @httpBodyArg() with other non-auth args is not supported.
						const hasOtherPayloadArgs = plan.some(
							(p) => p.source.kind !== 'userId' && p.source.kind !== 'userIdOptional' && p.source.kind !== 'body'
						);
						if (hasOtherPayloadArgs) {
							throw new Error('Cannot combine httpBodyArg with other request args in a single POST call');
						}

						const bodyValue = args[bodyArg.index];
						return await sendRequest({ method: 'POST', url: urlBase, body: bodyValue });
					}

					const body: Record<string, unknown> = {};

					for (const { index, source } of plan) {
						if (source.kind === 'userId' || source.kind === 'userIdOptional') {
							continue;
						}

						const value = args[index];
						const name = source.kind === 'arg' ? (source.name ?? `arg${index}`) : `arg${index}`;
						body[name] = value;
					}

					return await sendRequest({ method: 'POST', url: urlBase, body });
				};
			},
		}
	);

	return proxy as unknown as TService;
}


